import React, { useState, useEffect } from 'react';

import { Loader, useMantineTheme, MultiSelect, MultiSelectProps, SelectItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompletePackageForExpectations from 'hooks/reports/utilization/package-utilization/useAutoCompletePackage';
import { getFilteredMultiSelectPrevState } from 'utils/functions';

interface PackageMultiSelectProps extends Omit<MultiSelectProps, 'data'> {
  hasExpectation?: boolean;
  limit?: number;
}

const PackageForExpectationsMultiSelect = ({
  hasExpectation,
  limit,
  ...multiSelectProps
}: PackageMultiSelectProps): JSX.Element => {
  const theme = useMantineTheme();

  const [autocompleteData, setAutocompleteData] = useState<SelectItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 250);

  const { data, isSuccess, isLoading, isError } = useAutoCompletePackageForExpectations({
    search: debouncedSearchTerm,
    hasExpectation,
    limit: limit ?? 20,
    offset: 0,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const resultsFormattedData = data.data.results.map(packageInstance => ({
        value: packageInstance.packageId,
        label: packageInstance.name,
      }));
      const filteredPrevData = (prevState: SelectItem[]): SelectItem[] =>
        getFilteredMultiSelectPrevState(prevState, resultsFormattedData, multiSelectProps.value ?? []);
      setAutocompleteData(prevState => [...filteredPrevData(prevState), ...resultsFormattedData]);
    }
  }, [isSuccess, data, multiSelectProps.value]);

  const nothingFound = isLoading
    ? 'Packages are loading'
    : isError
    ? 'Error loading packages'
    : 'Packages were not found';

  return (
    <MultiSelect
      onSearchChange={setSearchTerm}
      data={autocompleteData}
      nothingFound={nothingFound}
      rightSection={isLoading && <Loader size={theme.fontSizes.md} />}
      searchable
      {...multiSelectProps}
    />
  );
};

export default PackageForExpectationsMultiSelect;
